import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2, X, SearchX } from 'lucide-react';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// États de chargement, d'erreur et de vide
// ---------------------------------------------------------------------------

export function Spinner({ className }: { className?: string }) {
    return (
        <Loader2
            className={cn('w-5 h-5 animate-spin text-[var(--color-primary)]', className)}
            aria-hidden
        />
    );
}

export function LoadingState({ label = 'Chargement…' }: { label?: string }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-16" role="status">
            <Spinner className="w-7 h-7" />
            <p className="text-sm text-[var(--color-text-secondary)]">{label}</p>
        </div>
    );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center" role="alert">
            <AlertCircle className="w-8 h-8 text-[var(--color-error)]" aria-hidden />
            <div>
                <p className="font-medium text-[var(--color-text)]">Une erreur est survenue</p>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)] max-w-md">{message}</p>
            </div>
            {onRetry && (
                <button type="button" onClick={onRetry} className="btn btn-secondary">
                    Réessayer
                </button>
            )}
        </div>
    );
}

interface EmptyStateProps {
    title: string;
    description?: string;
    action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--color-bg-hover)]">
                <SearchX className="w-6 h-6 text-[var(--color-text-muted)]" aria-hidden />
            </div>
            <div>
                <p className="font-medium text-[var(--color-text)]">{title}</p>
                {description && (
                    <p className="mt-1 text-sm text-[var(--color-text-secondary)] max-w-md">
                        {description}
                    </p>
                )}
            </div>
            {action}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Notifications éphémères
// ---------------------------------------------------------------------------

type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
    id: number;
    variant: ToastVariant;
    message: string;
}

interface ToastContextValue {
    notify: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, { icon: typeof Info; color: string; bg: string }> = {
    success: { icon: CheckCircle2, color: 'var(--color-success)', bg: 'var(--color-success-light)' },
    error: { icon: AlertCircle, color: 'var(--color-error)', bg: 'var(--color-error-light)' },
    info: { icon: Info, color: 'var(--color-info)', bg: 'var(--color-info-light)' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const dismiss = useCallback((id: number) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);

    const notify = useCallback(
        (message: string, variant: ToastVariant = 'success') => {
            const id = Date.now() + Math.random();
            setToasts((current) => [...current, { id, variant, message }]);
            window.setTimeout(() => dismiss(id), 5000);
        },
        [dismiss]
    );

    const value = useMemo(() => ({ notify }), [notify]);

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div
                className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm"
                role="region"
                aria-label="Notifications"
            >
                {toasts.map((toast) => {
                    const style = VARIANT_STYLES[toast.variant];
                    const Icon = style.icon;

                    return (
                        <div
                            key={toast.id}
                            role="status"
                            className="card flex items-start gap-3 p-3 pr-2 animate-slideUp shadow-lg"
                            style={{ borderLeft: `3px solid ${style.color}`, backgroundColor: style.bg }}
                        >
                            <Icon
                                className="w-4 h-4 mt-0.5 shrink-0"
                                style={{ color: style.color }}
                                aria-hidden
                            />
                            <p className="flex-1 text-sm text-[var(--color-text)]">{toast.message}</p>
                            <button
                                type="button"
                                onClick={() => dismiss(toast.id)}
                                className="p-1 rounded hover:bg-black/5"
                                aria-label="Masquer"
                            >
                                <X className="w-3.5 h-3.5 text-[var(--color-text-secondary)]" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast doit être utilisé à l’intérieur de ToastProvider');
    }
    return context;
}
